import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Camera, QrCode, Package, AlertCircle, CheckCircle, Loader, Clock, MapPin, ShoppingCart, AlertTriangle } from 'lucide-react';
import jsQR from 'jsqr';
import { useKeyboardWedge } from '@/components/BarcodeScanner';

import { API } from '@/config';

export default function LicensePlateScannerPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [stream, setStream] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const [palletInfo, setPalletInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [allocationType, setAllocationType] = useState('sales_order'); // 'sales_order' or 'work_order'
  const [scanningLocation, setScanningLocation] = useState(false);
  const [photos, setPhotos] = useState([]);
  const scanIntervalRef = useRef(null);
  const locationScanIntervalRef = useRef(null);
  const locationVideoRef = useRef(null);
  const locationCanvasRef = useRef(null);

  // Hardware barcode scanner (keyboard wedge) support
  useKeyboardWedge((code) => {
    if (!palletInfo) { // Only auto-lookup if not already viewing a pallet
      handleQRCodeDetected(code);
    }
  });

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError(null);
      setScannedData(null);
      setPalletInfo(null);
      
      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      
      setScanning(true);
      
      // Start scanning loop
      scanIntervalRef.current = setInterval(() => {
        scanQRCode();
      }, 500); // Scan every 500ms
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Failed to access camera. Please ensure camera permissions are granted.');
    }
  };

  const stopScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setScanning(false);
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code && code.data) {
        // QR code detected
        handleQRCodeDetected(code.data);
      }
    }
  };

  const handleQRCodeDetected = async (data) => {
    // Stop scanning
    stopScanning();
    setScannedData(data);
    setLoading(true);
    
    try {
      // Fetch license plate info
      const response = await axios.get(`${API}/warehouse/license-plates/${data}`);
      setPalletInfo(response.data);
      setError(null);
      
      // Fetch transaction history
      fetchTransactions(data);
    } catch (err) {
      console.error('Error fetching pallet info:', err);
      setError(`License plate "${data}" not found in system.`);
      setPalletInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    const lpNumber = e.target.lpNumber.value.trim();
    if (!lpNumber) return;
    
    setLoading(true);
    setScannedData(lpNumber);
    
    try {
      const response = await axios.get(`${API}/warehouse/license-plates/${lpNumber}`);
      setPalletInfo(response.data);
      setError(null);
      
      // Fetch transaction history
      fetchTransactions(lpNumber);
    } catch (err) {
      console.error('Error fetching pallet info:', err);
      setError(`License plate "${lpNumber}" not found in system.`);
      setPalletInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (lpNumber) => {
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/warehouse/license-plates/${lpNumber}/transactions`);
      setTransactions(response.data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const resetScanner = () => {
    setScannedData(null);
    setPalletInfo(null);
    setError(null);
    setTransactions([]);
  };

  const handleHold = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    
    const reason = e.target.holdReason.value;
    const notes = e.target.holdNotes.value;
    
    try {
      await axios.post(`${API}/warehouse/license-plates/${scannedData}/hold`, {
        reason,
        notes,
        held_by: 'warehouse_user'
      });
      
      alert('License plate placed on hold successfully');
      setShowHoldModal(false);
      
      // Refresh pallet info and transactions
      const response = await axios.get(`${API}/warehouse/license-plates/${scannedData}`);
      setPalletInfo(response.data);
      fetchTransactions(scannedData);
    } catch (err) {
      console.error('Error placing hold:', err);
      alert(err.response?.data?.detail || 'Failed to place hold on license plate');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchOrders = async (itemType) => {
    setLoadingOrders(true);
    try {
      // Fetch both SOs and WOs
      const [soResponse, woResponse] = await Promise.all([
        axios.get(`${API}/warehouse/sales-orders?status=open`),
        axios.get(`${API}/work-orders`)
      ]);
      
      setSalesOrders(soResponse.data);
      // Filter work orders to only show scheduled or in_production
      setWorkOrders(woResponse.data.filter(wo => 
        wo.status === 'scheduled' || wo.status === 'in_production'
      ));
      
      // Set default allocation type based on item type
      if (itemType === 'raw_material' || itemType === 'packaging') {
        setAllocationType('work_order');
      } else {
        setAllocationType('sales_order');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setSalesOrders([]);
      setWorkOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    
    const orderNumber = e.target.orderNumber.value;
    const orderType = allocationType;
    
    try {
      await axios.post(`${API}/warehouse/license-plates/${scannedData}/allocate`, {
        order_number: orderNumber,
        order_type: orderType,
        user: 'warehouse_user'
      });
      
      alert(`License plate allocated to ${orderType === 'work_order' ? 'Work Order' : 'Sales Order'} ${orderNumber} successfully`);
      setShowAllocateModal(false);
      
      // Refresh pallet info and transactions
      const response = await axios.get(`${API}/warehouse/license-plates/${scannedData}`);
      setPalletInfo(response.data);
      fetchTransactions(scannedData);
    } catch (err) {
      console.error('Error allocating:', err);
      alert(err.response?.data?.detail || 'Failed to allocate license plate');
    } finally {
      setActionLoading(false);
    }
  };

  const startLocationScan = async () => {
    try {
      setScanningLocation(true);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (locationVideoRef.current) {
        locationVideoRef.current.srcObject = mediaStream;
        locationVideoRef.current.play();
      }
      
      locationScanIntervalRef.current = setInterval(() => {
        scanLocationBarcode();
      }, 500);
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Failed to access camera. Please ensure camera permissions are granted.');
    }
  };

  const stopLocationScan = () => {
    if (locationScanIntervalRef.current) {
      clearInterval(locationScanIntervalRef.current);
      locationScanIntervalRef.current = null;
    }
    
    if (locationVideoRef.current && locationVideoRef.current.srcObject) {
      locationVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      locationVideoRef.current.srcObject = null;
    }
    
    setScanningLocation(false);
  };

  const scanLocationBarcode = () => {
    if (!locationVideoRef.current || !locationCanvasRef.current) return;
    
    const video = locationVideoRef.current;
    const canvas = locationCanvasRef.current;
    const context = canvas.getContext('2d');
    
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code && code.data) {
        // Location barcode detected
        document.getElementById('newLocationInput').value = code.data;
        stopLocationScan();
      }
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    
    const newLocationId = e.target.newLocation.value;
    
    try {
      await axios.post(`${API}/warehouse/license-plates/${scannedData}/transfer`, {
        new_location_id: newLocationId,
        user: 'warehouse_user'
      });
      
      alert('License plate transferred successfully');
      setShowTransferModal(false);
      stopLocationScan();
      
      // Refresh pallet info and transactions
      const response = await axios.get(`${API}/warehouse/license-plates/${scannedData}`);
      setPalletInfo(response.data);
      fetchTransactions(scannedData);
    } catch (err) {
      console.error('Error transferring:', err);
      alert(err.response?.data?.detail || 'Failed to transfer license plate');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      timestamp: new Date().toISOString()
    }));
    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleSavePhotos = async () => {
    setActionLoading(true);
    
    try {
      // In a real implementation, you would upload photos to a server
      // For now, we'll just log the transaction
      await axios.post(`${API}/warehouse/license-plates/${scannedData}/hold`, {
        reason: 'Photo documentation added',
        notes: `Added ${photos.length} photo(s)`,
        held_by: 'warehouse_user'
      });
      
      alert(`${photos.length} photo(s) saved successfully`);
      setShowPhotosModal(false);
      setPhotos([]);
      
      // Refresh transactions
      fetchTransactions(scannedData);
    } catch (err) {
      console.error('Error saving photos:', err);
      alert('Failed to save photos');
    } finally {
      setActionLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'receipt': return <Package className="w-4 h-4" />;
      case 'transfer': return <MapPin className="w-4 h-4" />;
      case 'allocation': return <ShoppingCart className="w-4 h-4" />;
      case 'hold': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'receipt': return 'text-green-600 bg-green-50';
      case 'transfer': return 'text-blue-600 bg-blue-50';
      case 'allocation': return 'text-purple-600 bg-purple-50';
      case 'hold': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/warehouse')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <QrCode className="w-6 h-6" />
                License Plate Scanner
              </h1>
              <p className="text-sm text-gray-600 mt-1">Scan or enter license plate number</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Scanner Section */}
        {!scannedData && (
          <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
            {!scanning ? (
              <>
                <button
                  onClick={startScanning}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
                >
                  <Camera className="w-6 h-6" />
                  Start Camera Scan
                </button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">OR</span>
                  </div>
                </div>
                
                <form onSubmit={handleManualEntry} className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter License Plate Manually
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="lpNumber"
                      placeholder="e.g., LP-20251020-0001"
                      className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      required
                    />
                    <button
                      type="submit"
                      className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium"
                    >
                      Look Up
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    autoPlay
                    playsInline
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="border-4 border-blue-500 rounded-lg" style={{ width: '250px', height: '250px' }}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <QrCode className="w-16 h-16 text-blue-500 animate-pulse" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-gray-600 mb-3">Position QR code within the frame</p>
                  <button
                    onClick={stopScanning}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Stop Scanning
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <Loader className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
            <p className="text-gray-600">Looking up license plate...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">Not Found</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
            <button
              onClick={resetScanner}
              className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Scan Another
            </button>
          </div>
        )}

        {/* Pallet Info Display */}
        {palletInfo && !loading && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-green-600 text-white p-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                <div>
                  <h2 className="font-bold text-lg">License Plate Found</h2>
                  <p className="text-sm opacity-90">{scannedData}</p>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Item</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Package className="w-4 h-4 text-gray-400" />
                      <p className="font-semibold">{palletInfo.item_name}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Lot Number</label>
                    <p className="font-mono font-semibold mt-1">{palletInfo.lot_number}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Quantity</label>
                    <p className="font-semibold mt-1">{palletInfo.quantity} {palletInfo.uom}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Status</label>
                    <p className="mt-1">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        palletInfo.status === 'active' ? 'bg-green-100 text-green-700' : 
                        palletInfo.status === 'on_hold' ? 'bg-red-100 text-red-700' :
                        palletInfo.status === 'allocated' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {palletInfo.status.toUpperCase().replace('_', ' ')}
                      </span>
                    </p>
                  </div>
                </div>
                
                {palletInfo.location_id && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Current Location</label>
                    <p className="font-mono font-semibold mt-1">{palletInfo.location_id}</p>
                  </div>
                )}
                
                {palletInfo.expiration_date && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Expiration Date</label>
                    <p className="font-semibold mt-1">
                      {new Date(palletInfo.expiration_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                
                {palletInfo.temperature && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Temperature</label>
                    <p className="font-semibold mt-1">{palletInfo.temperature}°F</p>
                  </div>
                )}
                
                {palletInfo.created_at && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Created Date</label>
                    <p className="font-semibold mt-1">
                      {new Date(palletInfo.created_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="border-t p-4">
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <button
                    onClick={() => setShowHoldModal(true)}
                    disabled={palletInfo.status === 'on_hold'}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    QA Hold
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowAllocateModal(true);
                      const itemType = palletInfo?.inventory?.item_type || 'finished_good';
                      fetchOrders(itemType);
                    }}
                    disabled={palletInfo.status === 'on_hold' || palletInfo.status === 'allocated'}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Allocate
                  </button>
                  
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <MapPin className="w-4 h-4" />
                    Transfer
                  </button>
                  
                  <button
                    onClick={() => setShowPhotosModal(true)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <Camera className="w-4 h-4" />
                    Photos
                  </button>
                </div>
                
                <button
                  onClick={resetScanner}
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium"
                >
                  Scan Another
                </button>
              </div>
            </div>

            {/* Event Log */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-gray-800 text-white p-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <h3 className="font-bold">Event Log</h3>
              </div>
              
              <div className="p-4">
                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <Loader className="w-8 h-8 mx-auto mb-2 text-gray-400 animate-spin" />
                    <p className="text-sm text-gray-500">Loading transaction history...</p>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No transaction history available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((transaction, index) => (
                      <div key={index} className="flex gap-3 pb-3 border-b last:border-b-0">
                        <div className={`p-2 rounded-lg ${getTransactionColor(transaction.transaction_type)}`}>
                          {getTransactionIcon(transaction.transaction_type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-sm capitalize">
                                {transaction.transaction_type.replace('_', ' ')}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {transaction.notes || `${transaction.transaction_type} transaction`}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(transaction.timestamp).toLocaleString()}
                            </span>
                          </div>
                          {transaction.reference_id && (
                            <p className="text-xs text-gray-500 mt-1">
                              Ref: {transaction.reference_id}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            User: {transaction.user}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* QA Hold Modal */}
      {showHoldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Place QA Hold
              </h3>
              <button
                onClick={() => setShowHoldModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleHold} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hold Reason *
                </label>
                <select
                  name="holdReason"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="Temperature out of range">Temperature out of range</option>
                  <option value="Product damage">Product damage</option>
                  <option value="Incorrect labeling">Incorrect labeling</option>
                  <option value="Foreign material detected">Foreign material detected</option>
                  <option value="Packaging defect">Packaging defect</option>
                  <option value="Color/appearance issue">Color/appearance issue</option>
                  <option value="Wrong item received">Wrong item received</option>
                  <option value="Quantity discrepancy">Quantity discrepancy</option>
                  <option value="Expired or near expiry">Expired or near expiry</option>
                  <option value="Missing documentation">Missing documentation</option>
                  <option value="Failed metal detection">Failed metal detection</option>
                  <option value="Cross-contamination risk">Cross-contamination risk</option>
                  <option value="Other">Other (specify in notes)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  name="holdNotes"
                  rows="3"
                  placeholder="Additional details..."
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This pallet will be placed on hold and cannot be used until QA releases it.
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowHoldModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Placing Hold...' : 'Place Hold'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Allocate to Order Modal */}
      {showAllocateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
                Allocate Pallet
              </h3>
              <button
                onClick={() => setShowAllocateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleAllocate} className="p-4 space-y-4">
              {/* Allocation Type Tabs */}
              <div className="flex border-b">
                <button
                  type="button"
                  onClick={() => setAllocationType('sales_order')}
                  className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    allocationType === 'sales_order'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Sales Orders
                </button>
                <button
                  type="button"
                  onClick={() => setAllocationType('work_order')}
                  className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    allocationType === 'work_order'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Work Orders
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select {allocationType === 'sales_order' ? 'Sales Order' : 'Work Order'} *
                </label>
                {loadingOrders ? (
                  <div className="text-center py-4">
                    <Loader className="w-6 h-6 mx-auto animate-spin text-purple-600" />
                    <p className="text-sm text-gray-500 mt-2">Loading orders...</p>
                  </div>
                ) : allocationType === 'sales_order' ? (
                  salesOrders.length === 0 ? (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm text-gray-500">No open sales orders available</p>
                    </div>
                  ) : (
                    <select
                      name="orderNumber"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Select a sales order...</option>
                      {salesOrders.map((order) => (
                        <option key={order.so_id} value={order.so_number}>
                          {order.so_number} - {order.customer_name}
                        </option>
                      ))}
                    </select>
                  )
                ) : (
                  workOrders.length === 0 ? (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm text-gray-500">No active work orders available</p>
                    </div>
                  ) : (
                    <select
                      name="orderNumber"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Select a work order...</option>
                      {workOrders.map((order) => (
                        <option key={order.wo_id} value={order.wo_number}>
                          {order.wo_number} - {order.item_name} ({order.production_line})
                        </option>
                      ))}
                    </select>
                  )
                )}
              </div>
              
              {/* Order Preview */}
              {(allocationType === 'sales_order' && salesOrders.length > 0) && (
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-3 py-2 border-b">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Order Preview</p>
                  </div>
                  <div className="p-3 space-y-2 max-h-40 overflow-y-auto">
                    {salesOrders.slice(0, 3).map((order) => (
                      <div key={order.so_id} className="text-xs space-y-1 pb-2 border-b last:border-b-0">
                        <div className="flex justify-between">
                          <span className="font-semibold">{order.so_number}</span>
                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">
                            {order.status?.toUpperCase() || 'OPEN'}
                          </span>
                        </div>
                        <div className="text-gray-600">{order.customer_name}</div>
                        <div className="text-gray-500">Ship Date: {(order.requested_ship_date || order.ship_date) ? (order.requested_ship_date || order.ship_date).replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${parseInt(m)}/${parseInt(d)}/${y}`) : 'TBD'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(allocationType === 'work_order' && workOrders.length > 0) && (
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-3 py-2 border-b">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Work Order Preview</p>
                  </div>
                  <div className="p-3 space-y-2 max-h-40 overflow-y-auto">
                    {workOrders.slice(0, 3).map((order) => (
                      <div key={order.wo_id} className="text-xs space-y-1 pb-2 border-b last:border-b-0">
                        <div className="flex justify-between">
                          <span className="font-semibold">{order.wo_number}</span>
                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                            {order.status?.toUpperCase().replace('_', ' ')}
                          </span>
                        </div>
                        <div className="text-gray-600">{order.item_name}</div>
                        <div className="text-gray-500">{order.production_line} - Qty: {order.planned_quantity}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  <strong>Note:</strong> This pallet will be reserved for the selected {allocationType === 'sales_order' ? 'sales order' : 'work order'} and won't be available for other allocations.
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAllocateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || (allocationType === 'sales_order' ? salesOrders.length === 0 : workOrders.length === 0)}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Allocating...' : 'Allocate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Location Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Transfer Location
              </h3>
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  stopLocationScan();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleTransfer} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Location
                </label>
                <input
                  type="text"
                  value={palletInfo?.location_id || 'Unknown'}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Location ID *
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    name="newLocation"
                    id="newLocationInput"
                    placeholder="e.g., location-id-here"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    required
                  />
                  
                  {!scanningLocation ? (
                    <button
                      type="button"
                      onClick={startLocationScan}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <QrCode className="w-4 h-4" />
                      Scan Location Barcode
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '200px' }}>
                        <video
                          ref={locationVideoRef}
                          className="w-full h-full object-cover"
                          autoPlay
                          playsInline
                        />
                        <canvas ref={locationCanvasRef} className="hidden" />
                        
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="border-4 border-blue-500 rounded-lg" style={{ width: '150px', height: '150px' }}>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <QrCode className="w-12 h-12 text-blue-500 animate-pulse" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={stopLocationScan}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                      >
                        Stop Scanning
                      </button>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    Scan the barcode on the destination location or enter manually
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowTransferModal(false);
                    stopLocationScan();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Transferring...' : 'Transfer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Photos Modal */}
      {showPhotosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-600" />
                Add Photos
              </h3>
              <button
                onClick={() => {
                  setShowPhotosModal(false);
                  setPhotos([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Photos
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photoInput"
                />
                <label
                  htmlFor="photoInput"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
                >
                  <Camera className="w-5 h-5" />
                  Take/Select Photos
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  You can select multiple photos at once
                </p>
              </div>
              
              {photos.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Photos ({photos.length})
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.preview}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-40 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                          {new Date(photo.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {photos.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                  <Camera className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">No photos selected</p>
                  <p className="text-xs text-gray-400 mt-1">Click the button above to add photos</p>
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Tip:</strong> Add photos to document pallet condition, damage, or special handling requirements.
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPhotosModal(false);
                    setPhotos([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePhotos}
                  disabled={actionLoading || photos.length === 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : `Save ${photos.length} Photo${photos.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
